import { useEffect, useCallback } from 'react';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { auth } from '../config/firebase';
import { useAuthStore } from '../store/authStore';
import { FirebaseService } from '../services/firebaseService';
import { ProductosService } from '../services/productosService';
import { User } from '../types';
import toast from 'react-hot-toast';

export const useAuth = () => {
  const { 
    setUser, 
    setUserData, 
    setLoading, 
    setError, 
    setInitialized,
    initialized
  } = useAuthStore();

  const handleAuthStateChanged = useCallback(async (firebaseUser: FirebaseUser | null) => {
    try {
      setUser(firebaseUser);
      
      if (!firebaseUser) {
        setUserData(null);
        setInitialized(true);
        return;
      }

      // Try to get user data from Firestore by UID first
      let userData = await FirebaseService.getDocument<User>('users', firebaseUser.uid);
      
      // If user doesn't exist by UID, check if there's an existing user with the same email
      if (!userData && firebaseUser.email) {
        const existingUserByEmail = await FirebaseService.getUserByEmail(firebaseUser.email);
        if (existingUserByEmail) {
          // User exists with same email but different UID - this means they're trying to create a new account
          // We need to prevent this and show an error
          setError(`Ya existe una cuenta registrada con el email ${firebaseUser.email}. Por favor, inicia sesión con tu cuenta existente.`);
          setLoading(false);
          setInitialized(true);
          
          // Sign out the user to prevent confusion
          await auth.signOut();
          return;
        }
      }
      
      // If user doesn't exist in Firestore, create it
      if (!userData) {
        try {
          // Verificar que el usuario tenga email
          if (!firebaseUser.email) {
            throw new Error('Usuario no tiene email');
          }

          const newUserData: Omit<User, 'id'> = {
            uid: firebaseUser.uid,
            nombre: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Usuario',
            email: firebaseUser.email,
            rol: 'owner',
            plan: 'individual',
            tenantId: 'default',
            createdAt: new Date(),
          };
          
          // Crear documento de usuario
          await FirebaseService.createUserDocument(newUserData);

          // Inicializar productos y categorías por defecto para el nuevo usuario
          // Solo si el email está verificado
          if (firebaseUser.emailVerified) {
            try {
              await ProductosService.crearProductosIniciales(firebaseUser.email!);
              console.log('✅ Productos y categorías iniciales creados exitosamente');
            } catch (initError) {
              console.warn('⚠️ Error inicializando productos por defecto:', initError);
            }
          } else {
            console.log('⚠️ Email no verificado - productos iniciales se crearán después de la verificación');
          }
          
          userData = { ...newUserData, id: firebaseUser.uid };
          toast.success('Usuario creado correctamente');
        } catch (err) {
          console.error('Error creating user document:', err);
          toast.error('Error al crear el usuario en la base de datos');
          setError('Error al crear el usuario en la base de datos');
          return;
        }
      }
      
      // Si el usuario existe pero no tiene email verificado, mostrar advertencia
      if (userData && firebaseUser && !firebaseUser.emailVerified) {
        console.log('⚠️ Usuario con email no verificado');
        // No bloquear el acceso, pero mostrar advertencia
      }
      
      setUserData(userData);
    } catch (err) {
      console.error('Error loading user data:', err);
      toast.error('Error al cargar datos del usuario');
      setError('Error al cargar datos del usuario');
    }
  }, [setUser, setUserData, setError, setInitialized, setLoading]);

  useEffect(() => {
    if (initialized) {
      return;
    }

    const unsubscribe = onAuthStateChanged(
      auth,
      async (user) => {
        setLoading(true);
        await handleAuthStateChanged(user);
        setLoading(false);
        setInitialized(true);
      },
      (error) => {
        console.error('Auth state change error:', error);
        setError(error.message);
        setLoading(false);
        setInitialized(true);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [initialized, setLoading, setError, setInitialized, handleAuthStateChanged]);
};